import {remapCopiedNodeConfig} from './utils/copyNodeReferences';
import {isTextEntryTarget} from './utils/keyboard';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useLocation, useNavigate, useOutlet, useParams} from 'react-router-dom';
import ReactFlow, {
    addEdge,
    applyEdgeChanges,
    applyNodeChanges,
    Background,
    BackgroundVariant,
    Connection,
    Edge,
    EdgeChange,
    MarkerType,
    Node,
    NodeChange,
    ReactFlowProvider,
    useReactFlow
} from 'reactflow';
import 'reactflow/dist/style.css';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {alpha, useTheme} from '@mui/material/styles';

import DetailsLoading from 'crqlar-react-common/dist/components/DetailsLoading';
import UnsavedChangesDialog from 'crqlar-react-common/dist/components/UnsavedChangesDialog';
import {useUnsavedChangesWarning} from 'crqlar-react-common/dist/utils/useUnsavedChangesWarning';

import AutomationActivateDialog from './components/AutomationActivateDialog';
import AutomationDeleteDialog from './components/AutomationDeleteDialog';
import AutomationEditorHeader from './components/AutomationEditorHeader';
import AutomationPauseDialog from './components/AutomationPauseDialog';
import AutomationRunDialog from './components/AutomationRunDialog';
import AutomationRenameDialog from './components/AutomationRenameDialog';
import CustomNode from './components/CustomNode';
import EditorControlsPanel from './components/EditorControlsPanel';
import AutomationAgentPanel from './agent/AutomationAgentPanel';
import {useAutomationAgentEnabled} from './agent/useAutomationAgentEnabled';
import {agentHistoryLabel, applyAgentOperations} from './agent/draftOperations';
import {AgentDraft, AgentOperation, FLOW_CHANGED_MESSAGE} from './agent/types';
import EditorToolbarPanel from './components/EditorToolbarPanel';
import FlowActivityPanel from './components/FlowActivityPanel';
import LegacyAutomationBanner from './components/LegacyAutomationBanner';
import MigratedAutomationBanner from './components/MigratedAutomationBanner';
import InsertableEdge, {EdgeActionsContext} from './components/InsertableEdge';
import {NodeActions, NodeActionsContext} from './components/NodeActionsContext';
import {UnreachableNodesContext} from './components/UnreachableNodesContext';
import {TestRunSucceededContext, TestRunTrailContext} from './components/TestRunTrailContext';
import TestRunBanner from './components/TestRunBanner';
import {CANVAS_STRIP_HEIGHT} from './components/CanvasStrip';
import AddNodePickers from './components/addNode/AddNodePickers';
import {findOpenPosition} from './utils/nodePlacement';
import NodeConfigDialog from './components/NodeConfigDialog';
import TriggerFlowDialog, {TriggerRunRequest} from './components/TriggerFlowDialog';


import {isLegacyAutomationFlow} from './config/legacyAutomation';
import {isTriggerNode} from './config/nodeCategories';

import {computeAutoLayout} from './utils/autoLayout';
import {automationsBasePath} from './utils/automationPaths';
import {
    fromFlowEdges,
    fromFlowNodes,
    nodePositionsSignature,
    reachabilityKey,
    removeNodesAndHeal,
    stepLabel,
    toFlowEdges,
    toFlowNodes,
    unreachableNodeIds,
    upstreamNodeData
} from './utils/flowGraph';

import {useAutomationFlow} from './hooks/useAutomationFlow';
import {createSnapshot, EditorSnapshot, useEditorHistory} from './hooks/useEditorHistory';
import {useNodeOperations} from './hooks/useNodeOperations';
import {AddNodePicker, useAddNodeGestures} from './hooks/useAddNodeGestures';
import {useEditorManualRun} from './hooks/useEditorManualRun';
import {useNodeDialogCatalogues} from './hooks/useNodeDialogCatalogues';
import {useTestRunWatch} from './hooks/useTestRunWatch';
import {useTestRunTrail} from './hooks/useTestRunTrail';
import {testRunSucceeded} from './utils/testRunState';
import type {TestRunSubject} from './utils/testRunState';
import {useFlowPreview} from './preview/useFlowPreview';
import {FlowPreviewCards, FlowPreviewContext} from './preview/FlowPreviewContext';
import StepPreviewDialog from './preview/StepPreviewDialog';
import PreviewBanner from './preview/PreviewBanner';
import {FLOW_ACTIVITY_QUERY_KEY, useFlowActivity} from './hooks/useFlowActivity';
import {nextScheduledRun} from './utils/nextScheduledRun';
import {remindNowOutcome, remindsNow} from './utils/remindNow';
import {AutomationNode, NodeConnection, NodeType} from "crqlar-react-common/dist/models/automation/automation";
import {useMutation, useQueryClient} from "react-query";
import {AutomationService} from "crqlar-react-common/dist/services/automation/AutomationService";
import {getErrorMessage} from "crqlar-react-common/dist/utils/http.util";
import {useSnackbar} from "notistack";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import {useTranslation} from "react-i18next";
import {useFullscreenSnackbarAnchor} from "../../layout/DashboardSnackbarProvider";

const nodeTypes = {
    custom: CustomNode
};

const edgeTypes = {
    insertable: InsertableEdge
};

const DUPLICATE_OFFSET = {x: 40, y: 40};

/** The API refuses a step connected to itself. */
const isValidConnection = (connection: Connection) => connection.source !== connection.target;

/** A flow's graph as the server holds it, comparable across responses (Dates stringify to ISO). */
const graphSignature = (nodes?: AutomationNode[], connections?: NodeConnection[]) => (
    JSON.stringify([nodes ?? [], connections ?? []])
);

interface NodeDialogGraph {
    upstreamNodes: AutomationNode[];
    flowNodes: AutomationNode[];
    flowConnections: NodeConnection[];
}

const NO_DIALOG_GRAPH: NodeDialogGraph = {upstreamNodes: [], flowNodes: [], flowConnections: []};

function AutomationDetailsContent() {
    const {flowId, appId} = useParams<{ flowId: string; appId: string }>();
    const navigate = useNavigate();
    const {pathname} = useLocation();
    const basePath = automationsBasePath(pathname);
    const {zoomIn, zoomOut, fitView, getNodes} = useReactFlow();

    // Runs are a child route (`:flowId/runs[/:executionId]`) laid over the canvas, so the editor stays
    // mounted with its unsaved changes, viewport and undo history.
    const executionsOutlet = useOutlet();
    const runsShown = !!executionsOutlet;
    const {enqueueSnackbar} = useSnackbar();
    const {t} = useTranslation();
    const theme = useTheme();
    const queryClient = useQueryClient();

    const readOnly = useReadOnly();

    const [nodes, setNodes] = useState<Node[]>([]);
    const [edges, setEdges] = useState<Edge[]>([]);
    // Every card reads this set, so it changes only with the structure — never on a drag frame.
    const reachability = reachabilityKey(nodes, edges);
    const unreachableNodes = useMemo(
        () => unreachableNodeIds(nodes, edges),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [reachability]
    );
    const [agentOpen, setAgentOpen] = useState(false);
    const agentEnabled = useAutomationAgentEnabled();
    const [addPicker, setAddPicker] = useState<AddNodePicker | null>(null);
    const [triggerDialogOpen, setTriggerDialogOpen] = useState(false);
    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
    const [renameDialogOpen, setRenameDialogOpen] = useState(false);
    const [renameError, setRenameError] = useState('');
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [activateDialogOpen, setActivateDialogOpen] = useState(false);
    const [pauseDialogOpen, setPauseDialogOpen] = useState(false);
    const [isDirty, setIsDirty] = useState(false);

    const edgeStyle = useMemo(
        () => ({stroke: theme.palette.grey[400], strokeWidth: 2}),
        [theme]
    );

    const edgeDefaults = useMemo(() => ({
        type: 'insertable',
        animated: true,
        deletable: true,
        style: edgeStyle,
        markerEnd: {
            type: MarkerType.ArrowClosed,
            color: edgeStyle.stroke,
            width: 18,
            height: 18,
        },
    }), [edgeStyle]);

    const {availableForms, formsLoading, availableSurveys, surveysLoading} = useNodeDialogCatalogues();

    const {flow, isLoading, saveMutation, renameMutation} = useAutomationFlow(flowId);

    const triggerNodes = flow?.nodes?.filter(node => isTriggerNode(node.type)) || [];

    const onViewExecution = useCallback((executionId: string) => {
        navigate(`${basePath}/${appId}/${flowId}/runs/${executionId}`);
    }, [navigate, basePath, appId, flowId]);

    // Feeds the activity card only (never the canvas or the test-run strip); not polled under the runs overlay.
    const {data: activity, dataUpdatedAt: activityUpdatedAt} = useFlowActivity(flowId, !!flow && !runsShown);

    // No endpoint returns the next scheduled run: computed with the API's ScheduleRules logic, and
    // recomputed on every activity poll so the countdown advances.
    const nextRun = useMemo(
        () => {
            const config = flow?.nodes?.find(node => node.type === NodeType.SCHEDULED_TRIGGER)?.config;
            return config ? nextScheduledRun(config, flow?.lastScheduledExecutionAt, new Date()) : null;
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [flow?.nodes, flow?.lastScheduledExecutionAt, activityUpdatedAt]
    );

    // `lastScheduledExecutionAt` comes only with the flow: refetch it on each activity update while the run is due.
    const nextRunDue = !!nextRun?.due;
    useEffect(() => {
        if (nextRunDue) void queryClient.invalidateQueries(['automation', flowId]);
    }, [nextRunDue, activityUpdatedAt, flowId, queryClient]);

    // The header run button starts a test run whatever the flow's status (AUTOMATIONS_UI.md, *Test run*).
    const testRunWatch = useTestRunWatch();
    const testRun = testRunWatch.run;
    const closeTestRun = testRunWatch.close;

    // Preview: what the canvas would do if it ran now. Off whenever the editor opens; editing stays possible.
    const [previewOn, setPreviewOn] = useState(false);
    const flowPreview = useFlowPreview(flowId, nodes, edges, previewOn);
    const previewable = flowPreview.previewable;
    // Without a trigger there is nothing to preview: Preview ends, rather than resuming unseen later.
    useEffect(() => {
        if (previewOn && !previewable) setPreviewOn(false);
    }, [previewOn, previewable]);
    const [previewStepId, setPreviewStepId] = useState<string | null>(null);
    const previewCards = useMemo<FlowPreviewCards>(() => ({
        preview: flowPreview.preview,
        loading: flowPreview.loading,
        refreshing: flowPreview.slow,
        onOpen: setPreviewStepId,
    }), [flowPreview.preview, flowPreview.loading, flowPreview.slow]);
    const previewAudienceName = useMemo(() => {
        const audience = nodes.find(node => node.data?.type === NodeType.AUDIENCE);
        return audience ? (audience.data.config?.audienceName || audience.data.label || undefined) : undefined;
    }, [nodes]);

    // Esc closes the strip unless a MUI dialog already handled it (defaultPrevented) or the runs overlay is up.
    const stripShown = previewOn || !!testRun;
    useEffect(() => {
        if (!stripShown || runsShown) return;
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key !== 'Escape' || event.defaultPrevented) return;
            setPreviewOn(false);
            closeTestRun();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [stripShown, runsShown, closeTestRun]);

    // One strip at a time: Preview closes the test-run strip, a test leaves Preview.
    const togglePreview = () => {
        if (!previewOn) closeTestRun();
        setPreviewOn(!previewOn);
    };

    const triggerMutation = useMutation({
        mutationFn: ({request}: {request: TriggerRunRequest; subject: TestRunSubject}) => {
            if (!flowId) throw new Error('Flow ID is required');
            return AutomationService.triggerFlow(flowId, request);
        },
        // No toast on purpose (AUTOMATIONS_UI.md, Decisions): the strip reports progress, the dialog a failed start.
        onSuccess: (execution, {subject}) => {
            // The activity card shows the queued run now, not at the next poll.
            void queryClient.invalidateQueries([FLOW_ACTIVITY_QUERY_KEY, flowId]);
            setTriggerDialogOpen(false);
            setPreviewOn(false);
            if (execution?.id) testRunWatch.watch(execution.id, subject);
            // Except a "Remind now" test with nobody due: no run, so no strip to report it.
            else if (remindNowOutcome(execution)?.reminders === 0) enqueueSnackbar(t('Remind now nobody'), {variant: 'info'});
        }
    });

    const testRunTrail = useTestRunTrail(testRun, nodes, edges);
    const testRunDone = !!testRun && testRunSucceeded(testRun.state);

    const statusMutation = useMutation({
        mutationFn: ({activate}: {activate: boolean}) => (
            activate ? AutomationService.activateFlow(flowId!) : AutomationService.pauseFlow(flowId!)
        ),
        onSuccess: (_result, {activate}) => {
            enqueueSnackbar(
                activate ? t('Automation is live') : t('Automation paused'),
                {variant: 'success'}
            );
            setActivateDialogOpen(false);
            setPauseDialogOpen(false);
            void queryClient.invalidateQueries(['automation', flowId]);
        },
        onError: (error) => {
            enqueueSnackbar(getErrorMessage(error, t('The automation status could not be updated')), {variant: 'error'});
        }
    });

    const deleteMutation = useMutation({
        mutationFn: (id: string) => AutomationService.deleteFlow(id),
        onSuccess: () => {
            enqueueSnackbar(t('Automation deleted successfully'), {variant: 'success'});
            setDeleteConfirmOpen(false);
            setIsDirty(false);
            navigate(`${basePath}/${appId}`);
        },
        onError: (error) => {
            enqueueSnackbar(getErrorMessage(error, t('The automation could not be deleted')), {variant: 'error'});
        }
    });

    const editorPath = `${basePath}/${appId}/${flowId}`;

    // Editor and its runs overlay are one screen: moving between them is not leaving (no unsaved-changes prompt).
    const isEditorPath = useCallback((pathname: string) => (
        pathname === editorPath || pathname.startsWith(`${editorPath}/`)
    ), [editorPath]);

    const {showDialog, handleConfirmNavigation, handleCancelNavigation} = useUnsavedChangesWarning({
        hasUnsavedChanges: isDirty && !readOnly,
        isSaving: saveMutation.isLoading,
        isInternalNavigation: ({currentPathname, nextPathname}) => (
            isEditorPath(currentPathname) && isEditorPath(nextPathname)
        )
    });

    const editRevisionRef = React.useRef(0);

    const markEdited = useCallback(() => {
        editRevisionRef.current += 1;
        setIsDirty(true);
    }, []);

    const isDirtyRef = React.useRef(false);
    const seededFlowIdRef = React.useRef<string | undefined>(undefined);
    // The server graph (`graphSignature`) the canvas was last seeded from or saved as.
    const seededGraphRef = React.useRef<string | null>(null);

    const nodesRef = React.useRef<Node[]>(nodes);

    useEffect(() => {
        isDirtyRef.current = isDirty;
        nodesRef.current = nodes;
    });

    // False when the canvas was edited during the save (it stays dirty). The response becomes the seeded
    // graph, so its query-cache echo does not re-seed the canvas and wipe undo.
    const saveCanvas = async (): Promise<boolean> => {
        const savedRevision = editRevisionRef.current;
        const saved = await saveMutation.mutateAsync({nodes, edges});

        seededGraphRef.current = graphSignature(saved.nodes, saved.connections);
        if (editRevisionRef.current !== savedRevision) return false;

        setIsDirty(false);
        return true;
    };

    const handleSave = () => {
        saveCanvas().then(
            () => enqueueSnackbar(t('Automation saved successfully'), {variant: 'success'}),
            error => enqueueSnackbar(getErrorMessage(error, t('The automation could not be saved')), {variant: 'error'})
        );
    };

    const {
        editingNode,
        nodeConfig,
        setNodeConfig,
        nodeLabel,
        setNodeLabel,
        configDialogOpen,
        setConfigDialogOpen,
        openNodeEditor,
        handleSaveNodeConfig,
        handleDeleteNode,
        addNode,
        createNodeCopy
    } = useNodeOperations(markEdited);

    const applySnapshot = useCallback((snapshot: EditorSnapshot) => {
        setNodes(snapshot.nodes);
        setEdges(snapshot.edges);
        markEdited();
    }, [markEdited]);

    const {
        manualRunConfirmNodeId,
        setManualRunConfirmNodeId,
        getManualRunDisabledReason,
        runManualTrigger,
        manualRunLoading,
        manualRunNodeId
    } = useEditorManualRun({
        flowId,
        flowStatus: flow?.status,
        readOnly,
        nodes,
        edges,
        isDirty,
        saveCanvas,
        onViewExecution
    });

    // No dialog, picker, menu or overlay is open. Gates the canvas shortcuts (undo, Cmd+D, Backspace) and the agent's edits.
    const canvasIdle = !readOnly && !configDialogOpen && !triggerDialogOpen && !manualRunConfirmNodeId
        && !renameDialogOpen && !deleteConfirmOpen && !activateDialogOpen && !pauseDialogOpen && !runsShown
        && !showDialog && !previewStepId && !addPicker && !menuAnchor;

    const {
        canUndo,
        canRedo,
        undoLabel,
        redoLabel,
        capture,
        captureSnapshot,
        undo,
        redo,
        reset: resetHistory
    } = useEditorHistory({
        nodes,
        edges,
        shortcutsEnabled: canvasIdle,
        onRestore: applySnapshot
    });

    const addGestures = useAddNodeGestures({
        picker: addPicker,
        setPicker: setAddPicker,
        readOnly,
        nodes,
        edges,
        setNodes,
        setEdges,
        edgeDefaults,
        addNode,
        capture,
        markEdited
    });

    const dragSnapshotRef = React.useRef<EditorSnapshot | null>(null);

    // The graph owner applies one transaction against the latest canvas, even after a long AI request.
    const agentEditorRef = React.useRef({nodes, edges, flowId, canEdit: canvasIdle});
    agentEditorRef.current = {nodes, edges, flowId, canEdit: canvasIdle};
    const getAgentDraft = useCallback((): AgentDraft => {
        const current = agentEditorRef.current;
        return structuredClone({flowId: current.flowId || '', revision: editRevisionRef.current,
            nodes: fromFlowNodes(current.nodes), connections: fromFlowEdges(current.edges),
            selectedNodeIds: current.nodes.filter(node => node.selected).map(node => node.id)});
    }, []);
    const applyAgentChanges = useCallback((base: AgentDraft, operations: AgentOperation[]) => {
        const current = agentEditorRef.current;
        if (!current.canEdit || dragSnapshotRef.current) throw new Error('Finish your current edit, then ask me again.');
        const latest = getAgentDraft();
        if (base.flowId !== latest.flowId || base.revision !== latest.revision
            || JSON.stringify(base.nodes) !== JSON.stringify(latest.nodes)
            || JSON.stringify(base.connections) !== JSON.stringify(latest.connections)) {
            throw new Error(FLOW_CHANGED_MESSAGE);
        }
        const result = applyAgentOperations(latest, operations);
        if (!result.summary.length) return result;
        const nextNodes = toFlowNodes(result.nodes).map(node => ({
            ...current.nodes.find(existing => existing.id === node.id), ...node,
            selected: result.changedNodeIds.includes(node.id)
        }));
        const nextEdges = toFlowEdges(result.connections, edgeDefaults).map(edge => ({...edge,
            id: current.edges.find(existing => existing.source === edge.source && existing.target === edge.target
                && (existing.sourceHandle || '') === (edge.sourceHandle || ''))?.id || crypto.randomUUID()}));
        const replacingGraph = !current.nodes.some(existing => nextNodes.some(node => node.id === existing.id));
        if (replacingGraph) {
            const positions = computeAutoLayout(nextNodes, nextEdges);
            nextNodes.forEach(node => {if (positions[node.id]) node.position = positions[node.id];});
        }
        captureSnapshot(createSnapshot(current.nodes, current.edges), agentHistoryLabel(result.summary));
        setNodes(nextNodes);
        setEdges(nextEdges);
        markEdited();
        // Keep a second response in the same event loop from applying to stale React state.
        agentEditorRef.current = {...current, nodes: nextNodes, edges: nextEdges};
        if (replacingGraph) requestAnimationFrame(() => fitView({padding: 0.2, maxZoom: 1, duration: 200}));
        return result;
    }, [getAgentDraft, edgeDefaults, captureSnapshot, markEdited, fitView]);

    const loadedFlowId = flow?.id;
    const loadedNodes = flow?.nodes;
    const loadedConnections = flow?.connections;

    // The flow query also refetches and echoes every save: only a server graph the editor did not seed
    // or save replaces the canvas, and never a dirty one.
    useEffect(() => {
        if (!loadedFlowId) return;

        const graph = graphSignature(loadedNodes, loadedConnections);
        const isSeededFlow = seededFlowIdRef.current === loadedFlowId;
        if (isSeededFlow && (isDirtyRef.current || seededGraphRef.current === graph)) return;
        seededFlowIdRef.current = loadedFlowId;
        seededGraphRef.current = graph;

        if (loadedNodes) {
            setNodes(toFlowNodes(loadedNodes));
        }

        if (loadedConnections) {
            setEdges(toFlowEdges(loadedConnections, edgeDefaults));
        }

        setIsDirty(false);
        resetHistory();
    }, [loadedFlowId, loadedNodes, loadedConnections, edgeDefaults, resetHistory]);

    // Only a real move is dirty: React Flow ends a "drag" on every click, so compare against the pre-drag positions.
    const onNodesChange = (changes: NodeChange[]) => {
        const dragStarted = changes.some(change => change.type === 'position' && change.dragging === true);
        const dragEnded = changes.some(change => change.type === 'position' && change.dragging === false);
        const structuralChange = changes.some(change => change.type === 'add' || change.type === 'remove');

        if (dragStarted && !dragSnapshotRef.current) {
            dragSnapshotRef.current = createSnapshot(nodes, edges);
        }

        if (structuralChange) {
            capture();
        }

        setNodes((nds) => applyNodeChanges(changes, nds));

        // Canvas deletes heal here (the dialog's Delete in useNodeOperations). React Flow has already
        // removed the node's edges, so heal from this render's edges.
        const removedNodeIds = changes.flatMap(change => change.type === 'remove' ? [change.id] : []);

        if (removedNodeIds.length) {
            setEdges(current => removeNodesAndHeal(current, edges, removedNodeIds));
        }

        let moved = false;

        if (dragEnded) {
            const preDrag = dragSnapshotRef.current;
            dragSnapshotRef.current = null;

            moved = !!preDrag && nodePositionsSignature(preDrag.nodes)
                !== nodePositionsSignature(applyNodeChanges(changes, nodes));

            if (moved && preDrag) {
                captureSnapshot(preDrag);
            }
        }

        if (moved || structuralChange) {
            markEdited();
        }
    };

    const onEdgesChange = (changes: EdgeChange[]) => {
        const hasMeaningfulChange = changes.some(change =>
            change.type === 'add' ||
            change.type === 'remove'
        );

        if (hasMeaningfulChange) {
            capture();
        }

        setEdges((eds) => applyEdgeChanges(changes, eds));

        if (hasMeaningfulChange) {
            markEdited();
        }
    };

    const onConnect = (connection: Connection) => {
        if (readOnly) return;

        // An existing connection is a no-op. Only If has two handles; a saved plain edge comes back as "default".
        const fromIf = nodes.find(node => node.id === connection.source)?.data?.type === NodeType.IF_CONDITION;
        const exists = edges.some(edge => edge.source === connection.source && edge.target === connection.target
            && (!fromIf || edge.sourceHandle === connection.sourceHandle));
        if (exists) return;

        capture();
        const edge = {
            ...edgeDefaults,
            ...connection,
            id: `${connection.source}-${connection.target}-${Date.now()}`,
        };
        setEdges((eds) => addEdge(edge, eds));
        markEdited();
    };

    const duplicateNodes = useCallback((sources: Node[]) => {
        if (readOnly || !sources.length) return;

        capture();

        setNodes(current => {
            const placed = [...current];
            const copies: Node[] = [];

            sources.forEach(source => {
                const preferred = {
                    x: source.position.x + DUPLICATE_OFFSET.x,
                    y: source.position.y + DUPLICATE_OFFSET.y,
                };
                const copy = createNodeCopy(source, findOpenPosition(preferred, placed), placed);

                copies.push({...copy, selected: true});
                placed.push(copy);
            });

            const copiedIds = new Map(sources.map((source, index) => [source.id, copies[index].id]));
            const copiedLabels = new Map(sources.map((source, index) => [stepLabel(source), stepLabel(copies[index])]));
            return [
                ...current.map(node => (node.selected ? {...node, selected: false} : node)),
                ...copies.map(copy => ({...copy, data: {...copy.data,
                    config: remapCopiedNodeConfig(copy.data.config, copiedIds, copiedLabels)}})),
            ];
        });

        markEdited();
    }, [readOnly, capture, createNodeCopy, markEdited]);

    useEffect(() => {
        if (!canvasIdle) return;

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.repeat || event.defaultPrevented) return;
            if (!event.metaKey && !event.ctrlKey) return;
            if (event.key.toLowerCase() !== 'd') return;
            if (isTextEntryTarget(event.target)) return;

            event.preventDefault();
            duplicateNodes(nodesRef.current.filter(node => node.selected));
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [canvasIdle, duplicateNodes]);

    const handleResetView = () => fitView({padding: 0.2, maxZoom: 1, duration: 400});

    const handleAutoLayout = () => {
        if (readOnly) return;

        // getNodes() carries the measured heights the layout spaces cards by.
        const positions = computeAutoLayout(getNodes(), edges);
        if (!Object.keys(positions).length) return;

        const laidOut = nodes.map(node => {
            const position = positions[node.id];
            return position ? {...node, position} : node;
        });

        // A layout that moves nothing is not an edit, but the view still refits.
        if (nodePositionsSignature(laidOut) !== nodePositionsSignature(nodes)) {
            capture();
            setNodes(laidOut);
            markEdited();
        }

        window.setTimeout(() => fitView({padding: 0.2, maxZoom: 1, duration: 400}), 50);
    };

    // Computed only while the dialog is open (not per drag frame); keeps the last value while it fades out.
    const dialogGraphRef = React.useRef<NodeDialogGraph>(NO_DIALOG_GRAPH);
    const dialogGraph = useMemo(() => {
        if (configDialogOpen) {
            dialogGraphRef.current = {
                upstreamNodes: editingNode ? upstreamNodeData(editingNode.id, nodes, edges) : [],
                flowNodes: fromFlowNodes(nodes),
                flowConnections: fromFlowEdges(edges),
            };
        }
        return dialogGraphRef.current;
    }, [configDialogOpen, editingNode, nodes, edges]);

    const {onAppendClick, activeAppend} = addGestures;

    const nodeActions = useMemo<NodeActions>(() => ({
        readOnly,
        onEdit: openNodeEditor,
        onAppendClick,
        activeAppend,
        getManualRun: nodeId => {
            const disabledReason = getManualRunDisabledReason(nodeId);
            return {
                onRequestRun: () => setManualRunConfirmNodeId(nodeId),
                isRunning: manualRunLoading && manualRunNodeId === nodeId,
                disabled: !!disabledReason || manualRunLoading || saveMutation.isLoading || configDialogOpen,
                disabledReason,
                hasUnsavedChanges: isDirty,
            };
        },
    }), [readOnly, openNodeEditor, onAppendClick, activeAppend, getManualRunDisabledReason, setManualRunConfirmNodeId,
        manualRunLoading, manualRunNodeId, saveMutation.isLoading, configDialogOpen, isDirty]);

    const handleBack = () => navigate(`${basePath}/${appId}`);

    const handleTriggerFlowClick = () => {
        // Clears the previous attempt's error, which the dialog shows.
        triggerMutation.reset();
        setTriggerDialogOpen(true);
    };

    const handleDelete = () => {
        if (!flowId || deleteMutation.isLoading) return;
        deleteMutation.mutate(flowId);
    };

    const handleRename = (name: string) => {
        if (readOnly || renameMutation.isLoading) return;
        setRenameError('');
        renameMutation.mutate(name, {
            onSuccess: () => {
                setRenameDialogOpen(false);
                enqueueSnackbar(t('Automation renamed'), {variant: 'success'});
            },
            onError: error => setRenameError(getErrorMessage(error, t('The automation could not be renamed')))
        });
    };

    const header = (
        <AutomationEditorHeader
            flow={flow}
            readOnly={readOnly}
            isDirty={isDirty}
            onBack={handleBack}
            previewable={previewable}
            previewOn={previewOn}
            onTogglePreview={togglePreview}
            testRunBusy={testRunWatch.busy}
            onTestRun={handleTriggerFlowClick}
            onGoLive={() => setActivateDialogOpen(true)}
            saving={saveMutation.isLoading}
            onSave={handleSave}
            menuAnchor={menuAnchor}
            onMenuAnchorChange={setMenuAnchor}
            renameDisabled={!flow || readOnly || saveMutation.isLoading || statusMutation.isLoading
                || deleteMutation.isLoading || renameMutation.isLoading}
            onRename={() => {
                setRenameError('');
                setRenameDialogOpen(true);
            }}
            onPause={() => setPauseDialogOpen(true)}
            onDelete={() => setDeleteConfirmOpen(true)}
        />
    );

    const notFound = (
        <Stack alignItems={'center'} justifyContent={'center'} gap={1} sx={{flex: 1, minHeight: 0, p: 3}}>
            <Typography variant={'h6'}>
                {t('Automation not found')}
            </Typography>
            <Typography variant={'body2'} color={'text.secondary'} textAlign={'center'}>
                {t('This automation may have been deleted or you no longer have access to it.')}
            </Typography>
            <Button variant={'outlined'} onClick={handleBack} sx={{mt: 1}}>
                {t('Back to automations')}
            </Button>
        </Stack>
    );

    const editor = flow && (
        <Box
            sx={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                position: 'relative',
                overflow: 'hidden',
                backgroundColor: theme.palette.grey[50]
            }}
        >
            {/* Not mounted without access: a hidden panel would still run its live conversation. */}
            {agentEnabled && (
                <AutomationAgentPanel key={flowId} open={agentOpen} canEdit={canvasIdle}
                    onClose={() => setAgentOpen(false)} getDraft={getAgentDraft} applyChanges={applyAgentChanges}
                    selectedLabels={nodes.filter(node => node.selected).map(node => node.data.label || node.data.type)}/>
            )}
            {/* Canvas tint names the mode (blue preview, orange test run). The strip overlays the top
                edge so no card moves; only the top panels step down. */}
            <Box sx={{
                flex: 1,
                minWidth: 0,
                minHeight: 0,
                position: 'relative',
                bgcolor: previewOn
                    ? alpha(theme.palette.info.main, 0.05)
                    : testRun ? alpha(testRunDone ? theme.palette.success.main : theme.palette.warning.main, 0.04) : 'transparent',
                transition: 'background-color 200ms ease',
                '& .react-flow__panel.top': {
                    top: (previewOn && previewable) || (!previewOn && testRun) ? CANVAS_STRIP_HEIGHT : 0,
                    transition: 'top 200ms ease',
                },
            }}>
            {previewOn && previewable && (
                <PreviewBanner
                    calculating={flowPreview.loading || flowPreview.slow}
                    failed={flowPreview.failed}
                    readingName={previewAudienceName}
                    onRetry={flowPreview.retry}
                    onExit={() => setPreviewOn(false)}
                />
            )}
            {!previewOn && testRun && (
                <TestRunBanner
                    run={testRun}
                    skipping={testRunWatch.skipping}
                    skipFailed={testRunWatch.skipFailed}
                    onSkipWait={() => void testRunWatch.skipWait()}
                    onViewRun={onViewExecution}
                    onClose={closeTestRun}
                />
            )}
            <EdgeActionsContext.Provider value={addGestures.edgeActions}>
                <NodeActionsContext.Provider value={nodeActions}>
                <UnreachableNodesContext.Provider value={unreachableNodes}>
                <TestRunTrailContext.Provider value={testRunTrail}>
                <TestRunSucceededContext.Provider value={testRunDone}>
                <FlowPreviewContext.Provider value={previewCards}>
                    <ReactFlow
                        nodes={nodes}
                        edges={edges}
                        onNodesChange={onNodesChange}
                        onEdgesChange={onEdgesChange}
                        onConnect={onConnect}
                        isValidConnection={isValidConnection}
                        onDoubleClick={addGestures.onPaneDoubleClick}
                        // Double-click opens the picker (pane) or the node dialog (card), not zoom.
                        zoomOnDoubleClick={false}
                        nodeTypes={nodeTypes}
                        fitView
                        fitViewOptions={{padding: 0.2, maxZoom: 1}}
                        defaultViewport={{x: 0, y: 0, zoom: 0.8}}
                        nodesDraggable={!readOnly}
                        nodesConnectable={!readOnly}
                        // React Flow's Backspace listens on the whole document, so it must not delete
                        // behind an open dialog or picker. Undefined is its default key.
                        deleteKeyCode={canvasIdle ? undefined : null}
                        edgeTypes={edgeTypes}
                        defaultEdgeOptions={edgeDefaults}
                    >
                        <Background variant={BackgroundVariant.Dots} gap={12} size={1}/>


                        <EditorControlsPanel
                            onZoomIn={() => zoomIn({duration: 200})}
                            onZoomOut={() => zoomOut({duration: 200})}
                            onResetView={handleResetView}
                            onAutoLayout={handleAutoLayout}
                            autoLayoutDisabled={readOnly}
                            agentOpen={agentOpen}
                            onOpenAgent={() => setAgentOpen(true)}
                            agentEnabled={agentEnabled}
                        />

                        <FlowActivityPanel
                            flow={flow}
                            activity={activity}
                            nextRun={nextRun}
                            executionPath={executionId => `${basePath}/${appId}/${flowId}/runs/${executionId}`}
                            executionsPath={`${basePath}/${appId}/${flowId}/runs`}
                        />

                        {!readOnly && (
                            <EditorToolbarPanel
                                onAdd={addGestures.openToolbarPicker}
                                addActive={addPicker?.kind === 'toolbar'}
                                onUndo={undo}
                                onRedo={redo}
                                canUndo={canUndo}
                                canRedo={canRedo}
                                undoLabel={undoLabel}
                                redoLabel={redoLabel}
                            />
                        )}
                    </ReactFlow>
                </FlowPreviewContext.Provider>
                </TestRunSucceededContext.Provider>
                </TestRunTrailContext.Provider>
                </UnreachableNodesContext.Provider>
                </NodeActionsContext.Provider>
            </EdgeActionsContext.Provider>
            </Box>
        </Box>
    );

    return (
        <Box
            sx={{
                height: '100dvh',
                minHeight: 0,
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                boxSizing: 'border-box',
                overflow: 'hidden',
                bgcolor: 'background.default'
            }}
        >
            {header}

            {/* Banners stay outside React Flow so the editor below keeps the remaining height. */}
            {!!flow && isLegacyAutomationFlow(flow) && (
                <LegacyAutomationBanner flowId={flowId} appId={appId} basePath={basePath}/>
            )}

            <MigratedAutomationBanner flow={flow} flowId={flowId}/>

            {isLoading ? (
                <DetailsLoading/>
            ) : !flow ? (
                notFound
            ) : (
                editor
            )}

            {runsShown && (
                <Box
                    sx={{
                        position: 'fixed',
                        inset: 0,
                        zIndex: theme.zIndex.drawer,
                        bgcolor: 'background.default'
                    }}
                >
                    {executionsOutlet}
                </Box>
            )}

            <AddNodePickers {...addGestures.pickers}/>

            <NodeConfigDialog
                open={configDialogOpen}
                editingNode={editingNode}
                nodeConfig={nodeConfig}
                setNodeConfig={setNodeConfig}
                nodeLabel={nodeLabel}
                setNodeLabel={setNodeLabel}
                onClose={() => setConfigDialogOpen(false)}
                // Pass capture, don't call it: the save captures an undo step only when it changes something.
                onSave={(configOverride) => handleSaveNodeConfig(nodes, setNodes, configOverride, capture)}
                onDelete={() => {
                    capture();
                    handleDeleteNode(setNodes, setEdges);
                }}
                availableForms={availableForms}
                availableFormsLoading={formsLoading}
                availableSurveys={availableSurveys}
                availableSurveysLoading={surveysLoading}
                upstreamNodes={dialogGraph.upstreamNodes}
                flowNodes={dialogGraph.flowNodes}
                flowConnections={dialogGraph.flowConnections}
                readOnly={readOnly}
            />
            <TriggerFlowDialog
                open={triggerDialogOpen}
                onClose={() => setTriggerDialogOpen(false)}
                onRun={(request, subject) => triggerMutation.mutate({request, subject})}
                flowId={flowId || ''}
                isRunning={triggerMutation.isLoading}
                startFailed={triggerMutation.isError}
                triggerNodes={triggerNodes}
                flowNodes={flow?.nodes}
            />

            <StepPreviewDialog
                nodeId={previewStepId}
                onClose={() => setPreviewStepId(null)}
                preview={flowPreview.preview}
                nodes={nodes}
            />

            <AutomationRunDialog
                open={!!manualRunConfirmNodeId}
                automationName={flow?.name ?? ''}
                triggerName={nodes.find(node => node.id === manualRunConfirmNodeId)?.data.label || t('Manual Trigger')}
                remindNow={remindsNow(NodeType.MANUAL_TRIGGER, nodes.map(node => node.data?.type))}
                hasUnsavedChanges={isDirty}
                isRunning={manualRunLoading}
                disabledReason={manualRunConfirmNodeId ? getManualRunDisabledReason(manualRunConfirmNodeId) : undefined}
                onClose={() => {if (!manualRunLoading) setManualRunConfirmNodeId(null);}}
                onConfirm={() => {if (manualRunConfirmNodeId && !manualRunLoading) runManualTrigger(manualRunConfirmNodeId);}}
            />

            <AutomationActivateDialog
                open={activateDialogOpen}
                nodes={flow?.nodes ?? []}
                connections={flow?.connections}
                hasUnsavedChanges={isDirty}
                isActivating={statusMutation.isLoading}
                onClose={() => setActivateDialogOpen(false)}
                onConfirm={() => statusMutation.mutate({activate: true})}
            />

            <AutomationPauseDialog
                open={pauseDialogOpen}
                isPausing={statusMutation.isLoading}
                onClose={() => setPauseDialogOpen(false)}
                onConfirm={() => statusMutation.mutate({activate: false})}
            />

            {renameDialogOpen && flow && !readOnly && <AutomationRenameDialog
                key={flow.id}
                name={flow.name ?? ''}
                isRenaming={renameMutation.isLoading}
                error={renameError}
                onClose={() => setRenameDialogOpen(false)}
                onRename={handleRename}
            />}

            <AutomationDeleteDialog
                open={deleteConfirmOpen}
                name={flow?.name ?? ''}
                isDeleting={deleteMutation.isLoading}
                onClose={() => setDeleteConfirmOpen(false)}
                onConfirm={handleDelete}
            />

            <UnsavedChangesDialog
                open={showDialog}
                onConfirm={handleConfirmNavigation}
                onCancel={handleCancelNavigation}
            />
        </Box>
    );
}

export default function AutomationDetails() {
    useFullscreenSnackbarAnchor();

    return (
        <ReactFlowProvider>
            <AutomationDetailsContent/>
        </ReactFlowProvider>
    );
}
